import type { TFunction } from 'i18next';

import {
  FORMAT_COLOR_NAMES,
  FORMAT_COLORS,
  formatDate,
  formatInstant,
  type FieldType,
  type HistoryEvent,
} from '@/entities/database';
import type { DescribedEvent } from '@/shared/lib/journal-search';

/** One line under an entry: a changed value (`before` / `after`), a labelled text, or a note. */
export interface HistoryLine {
  label?: string;
  before?: string;
  after?: string;
  text?: string;
  /** Too long for the timeline: shown cut, the full text opens in the detail dialog. */
  long?: boolean;
}

const EVENT_KEYS = {
  'cell.changed': 'databases.history.events.cellChanged',
  'record.created': 'databases.history.events.recordCreated',
  'record.trashed': 'databases.history.events.recordTrashed',
  'record.restored': 'databases.history.events.recordRestored',
  'record.purged': 'databases.history.events.recordPurged',
  'record.moved': 'databases.history.events.recordMoved',
  'record.section_changed': 'databases.history.events.recordSection',
  'records.section_changed': 'databases.history.events.recordsSection',
  'section.created': 'databases.history.events.sectionCreated',
  'section.renamed': 'databases.history.events.sectionRenamed',
  'section.color_changed': 'databases.history.events.sectionColor',
  'section.moved': 'databases.history.events.sectionMoved',
  'section.deleted': 'databases.history.events.sectionDeleted',
  'format.applied': 'databases.history.events.formatApplied',
  'column.created': 'databases.history.events.columnCreated',
  'column.updated': 'databases.history.events.columnUpdated',
  'column.moved': 'databases.history.events.columnMoved',
  'column.trashed': 'databases.history.events.columnTrashed',
  'table.created': 'databases.history.events.tableCreated',
  'table.renamed': 'databases.history.events.tableRenamed',
  'table.moved': 'databases.history.events.tableMoved',
  'table.appearance_changed': 'databases.history.events.tableAppearance',
  'table.trashed': 'databases.history.events.tableTrashed',
  'table.duplicated': 'databases.history.events.tableDuplicated',
  'table.imported': 'databases.history.events.tableImported',
  'table.exported': 'databases.history.events.tableExported',
  'view.created': 'databases.history.events.viewCreated',
  'view.updated': 'databases.history.events.viewUpdated',
  'view.deleted': 'databases.history.events.viewDeleted',
  'database.created': 'databases.history.events.databaseCreated',
  'database.renamed': 'databases.history.events.databaseRenamed',
  'database.updated': 'databases.history.events.databaseUpdated',
  'database.moved': 'databases.history.events.databaseMoved',
  'database.appearance_changed': 'databases.history.events.databaseAppearance',
  'database.confidentiality_changed': 'databases.history.events.databaseConfidentiality',
  'database.owner_changed': 'databases.history.events.databaseOwnerChanged',
  'database.duplicated': 'databases.history.events.databaseDuplicated',
  'database.imported': 'databases.history.events.databaseImported',
  'database.exported': 'databases.history.events.databaseExported',
  'database.template_saved': 'databases.history.events.databaseTemplateSaved',
  'database.export_finished': 'databases.history.events.databaseExportFinished',
  'table.export_finished': 'databases.history.events.tableExportFinished',
  'database.copy_made': 'databases.history.events.databaseCopyMade',
  'table.copy_made': 'databases.history.events.tableCopyMade',
  'database.trashed': 'databases.history.events.databaseTrashed',
  'access.member_role_set': 'databases.history.events.accessMemberRoleSet',
  'access.member_removed': 'databases.history.events.accessMemberRemoved',
  'access.member_left': 'databases.history.events.accessMemberLeft',
  'access.denied': 'databases.history.events.accessDenied',
  'access.undenied': 'databases.history.events.accessUndenied',
  'access.rule_set': 'databases.history.events.accessRuleSet',
  'access.rule_resumed': 'databases.history.events.accessRuleResumed',
  'access.rule_deleted': 'databases.history.events.accessRuleDeleted',
  'access.role_permissions_changed': 'databases.history.events.accessRolePermissions',
  'access.user_exceptions_set': 'databases.history.events.accessUserExceptions',
  'access.workspace_changed': 'databases.history.events.accessWorkspaceChanged',
  'invitation.created': 'databases.history.events.invitationCreated',
  'invitation.accepted': 'databases.history.events.invitationAccepted',
  'invitation.declined': 'databases.history.events.invitationDeclined',
  'invitation.revoked': 'databases.history.events.invitationRevoked',
  'invitation.resent': 'databases.history.events.invitationResent',
} as const;

/** Every event kind with its description in the viewer's language (what History search matches). */
export function describedEvents(t: TFunction): DescribedEvent[] {
  return Object.entries(EVENT_KEYS).map(([type, key]) => ({
    type,
    text: (t as unknown as (key: string) => string)(key),
  }));
}

type EventKey =
  | (typeof EVENT_KEYS)[keyof typeof EVENT_KEYS]
  | 'databases.history.events.recordsCreatedImport'
  | 'databases.history.events.recordsCreatedDuplicate';

const EXPORT_STATUS = {
  requested: 'databases.history.exportStatus.requested',
  generated: 'databases.history.exportStatus.generated',
  failed: 'databases.history.exportStatus.failed',
  aborted: 'databases.history.exportStatus.aborted',
} as const;

const REASONS = {
  team_deleted: 'databases.history.reasons.team_deleted',
  team_member_added: 'databases.history.reasons.team_member_added',
  team_member_removed: 'databases.history.reasons.team_member_removed',
  role_updated: 'databases.history.reasons.role_updated',
  role_deleted: 'databases.history.reasons.role_deleted',
  role_permissions_changed: 'databases.history.reasons.role_permissions_changed',
  role_hierarchy_changed: 'databases.history.reasons.role_hierarchy_changed',
  member_role_changed: 'databases.history.reasons.member_role_changed',
  owner_changed: 'databases.history.reasons.owner_changed',
  left_workspace: 'databases.history.reasons.left_workspace',
} as const;

const FIELD_LABELS = {
  name: 'databases.history.fields.name',
  field_type: 'databases.history.fields.fieldType',
  wrap_text: 'databases.history.fields.wrap_text',
  frozen: 'databases.history.fields.frozen',
  color: 'databases.history.fields.color',
  filters: 'databases.history.fields.filters',
  sort: 'databases.history.fields.sort',
  hidden: 'databases.history.fields.hidden',
  order: 'databases.history.fields.order',
  config: 'databases.history.fields.config',
  description: 'databases.history.fields.description',
} as const;

const LONG = 140;

export interface Context {
  t: TFunction;
  locale: string;
  timeZone: string | undefined;
  /** Names of the users that payloads refer to. */
  users: Readonly<Record<string, string>>;
}

export interface Described {
  /** The one-sentence description. */
  text: string;
  lines: HistoryLine[];
}

type Payload = Record<string, unknown>;
const obj = (value: unknown): Payload =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Payload) : {};
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** A stored value as readable text (a label when one is known). */
export function show(
  ctx: Context,
  value: unknown,
  type: string | undefined,
  label?: unknown,
): string {
  const { t } = ctx;
  if (value === null || value === undefined || value === '') return t('databases.history.noValue');
  const named = Array.isArray(label) ? label.map(String).join(', ') : label;
  if (typeof named === 'string' && named !== '') return named;
  if (typeof value === 'boolean')
    return value ? t('databases.history.yes') : t('databases.history.no');
  if (typeof value === 'string') {
    if (type === 'date') return formatDate(value, ctx.locale);
    if (type === 'datetime') return formatInstant(value, ctx.locale, ctx.timeZone);
    return value;
  }
  if (Array.isArray(value)) return value.map(String).join(', ');
  return JSON.stringify(value);
}

function valueLine(ctx: Context, entry: Payload, label?: string): HistoryLine {
  const type = str(entry.field_type);
  const before = show(ctx, entry.before, type, entry.before_label);
  const after = show(ctx, entry.after, type, entry.after_label);
  const raw = entry.before ?? entry.after;
  const long =
    type === 'long_text' ||
    before.length > LONG ||
    after.length > LONG ||
    (typeof raw === 'string' && raw.includes('\n'));
  return { ...(label && { label }), before, after, long };
}

/** The palette name of an identification color ("none" when it is absent). */
function paletteName(t: TFunction, value: unknown): string {
  const index = FORMAT_COLORS.findIndex((color) => color === value);
  const name = FORMAT_COLOR_NAMES[index];
  return t(
    (name === undefined
      ? 'databases.palette.none'
      : `databases.palette.${name}`) as 'databases.palette.none',
  );
}

function role(ctx: Context, value: unknown): string {
  return typeof value === 'string' && value !== ''
    ? ctx.t(`roles.${value as 'owner'}`, { defaultValue: value })
    : ctx.t('databases.history.noRole');
}

function typeName(ctx: Context, value: unknown): string {
  const type = str(value);
  return type ? ctx.t(`databases.types.${type as FieldType}`, { defaultValue: type }) : '';
}

/** The name of a text-formatting property (`bold`, `size`, ...). */
function styleName(ctx: Context, prop: string): string {
  const key = `databases.history.styleProps.${prop}`;
  return ctx.t(key, { defaultValue: prop });
}

/** A formatting change of the formatting event: wrap, background or `style.<property>`. */
function formatLabel(ctx: Context, property: string): string {
  const { t } = ctx;
  if (property === 'wrap') return t('databases.history.fields.wrap');
  if (property.startsWith('style.')) {
    return `${t('databases.history.fields.textFormat')}: ${styleName(ctx, property.slice(6))}`;
  }
  return t('databases.history.fields.color');
}

function columnLines(ctx: Context, changes: Payload): HistoryLine[] {
  const { t } = ctx;
  const lines: HistoryLine[] = [];
  for (const [key, raw] of Object.entries(changes)) {
    const label = key in FIELD_LABELS ? t(FIELD_LABELS[key as keyof typeof FIELD_LABELS]) : key;
    const change = obj(raw);
    if (key === 'header_style') {
      // header appearance: one line per property that really changed
      const before = obj(change.before);
      const after = obj(change.after);
      for (const prop of new Set([...Object.keys(before), ...Object.keys(after)])) {
        lines.push({
          label: `${t('databases.history.fields.headerStyle')}: ${styleName(ctx, prop)}`,
          before: show(ctx, before[prop], undefined),
          after: show(ctx, after[prop], undefined),
        });
      }
      continue;
    }
    if (key === 'config' && typeof raw === 'object') {
      const options = obj(change.options);
      for (const part of ['added', 'removed', 'renamed', 'recolored', 'recategorised'] as const) {
        const items = list(options[part]);
        if (items.length === 0) continue;
        const names = items.map((item) => {
          const entry = obj(item);
          return part === 'renamed'
            ? `${str(entry.before)} → ${str(entry.after)}`
            : str(entry.label) || str(entry.id);
        });
        lines.push({ text: t(`databases.history.options.${part}`, { list: names.join(', ') }) });
      }
      if (options.reordered === true)
        lines.push({ text: t('databases.history.options.reordered') });
      for (const [setting, value] of Object.entries(change)) {
        if (setting === 'options') continue;
        const pair = obj(value);
        lines.push({
          label: setting,
          before: show(ctx, pair.before, undefined),
          after: show(ctx, pair.after, undefined),
        });
      }
      continue;
    }
    if (!('before' in change) && !('after' in change)) {
      lines.push({ text: label }); // a change recorded before UD-LOG: only that it happened
      continue;
    }
    const type = key === 'field_type';
    lines.push({
      label,
      before: type ? typeName(ctx, change.before) : show(ctx, change.before, undefined),
      after: type ? typeName(ctx, change.after) : show(ctx, change.after, undefined),
    });
  }
  return lines;
}

function cellLines(ctx: Context, cells: unknown, side: 'before' | 'after'): HistoryLine[] {
  const entries = list(cells).map(obj);
  const lines: HistoryLine[] = entries.slice(0, 6).map((entry) => {
    const type = str(entry.field_type);
    const text = show(ctx, entry[side], type, entry[`${side}_label`]);
    return {
      label: str(entry.column_name),
      text,
      long: text.length > LONG || type === 'long_text',
    };
  });
  if (entries.length > 6) {
    lines.push({ text: ctx.t('databases.history.moreCells', { count: entries.length - 6 }) });
  }
  return lines;
}

function viewLines(ctx: Context, changes: Payload): HistoryLine[] {
  return Object.entries(changes).map(([key, raw]) => {
    const pair = obj(raw);
    const label = key in FIELD_LABELS ? ctx.t(FIELD_LABELS[key as keyof typeof FIELD_LABELS]) : key;
    const render = (value: unknown) =>
      typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    const before = render(pair.before);
    const after = render(pair.after);
    return { label, before, after, long: true };
  });
}

/** Where a record was and is, by the section names recorded AT THAT MOMENT. */
function sectionLine(ctx: Context, p: Payload): HistoryLine {
  const none = ctx.t('databases.sections.noSection');
  return {
    label: ctx.t('databases.history.fields.section'),
    before: str(p.section_before) || none,
    after: str(p.section_after) || none,
  };
}

/** The sentence and the lines of one History entry, in the viewer's language. */
export function describe(event: HistoryEvent, ctx: Context): Described {
  const { t } = ctx;
  const p = event.payload;
  const actor = event.actor.name ?? t('databases.history.unknownActor');
  const user = event.subject?.name ?? t('databases.history.unknownActor');
  const params = {
    actor,
    user,
    table: str(p.table_name) || str(p.name),
    column: str(p.column_name) || str(p.name),
    view: str(p.name),
    section: str(p.name),
    count: typeof p.count === 'number' ? p.count : 0,
    owner: ctx.users[str(p.new_owner_id)] ?? user,
    role: str(p.role),
    type: event.event_type,
    status: '',
  };
  let lines: HistoryLine[] = [];
  let key: EventKey | undefined = (EVENT_KEYS as Record<string, EventKey>)[event.event_type];
  switch (event.event_type) {
    case 'cell.changed':
      lines = [valueLine(ctx, p)];
      break;
    case 'record.created':
      lines = cellLines(ctx, p.cells, 'after');
      break;
    case 'record.section_changed':
    case 'record.moved':
      if ('section_after_id' in p) lines = [sectionLine(ctx, p)];
      break;
    case 'records.section_changed':
      lines = [
        {
          label: t('databases.history.fields.section'),
          after: str(p.section_after) || t('databases.sections.noSection'),
        },
      ];
      break;
    case 'section.renamed':
      lines = [
        { label: t('databases.history.fields.name'), before: str(p.before), after: str(p.after) },
      ];
      break;
    case 'section.color_changed':
      lines = [
        {
          label: t('databases.history.fields.color'),
          before: show(ctx, p.before, undefined),
          after: show(ctx, p.after, undefined),
        },
      ];
      break;
    case 'section.created':
    case 'section.deleted':
      lines = [{ text: t('databases.history.sectionRecords', { count: Number(p.records ?? 0) }) }];
      break;
    case 'record.trashed':
      lines = cellLines(ctx, p.cells, 'before');
      break;
    case 'record.restored':
      lines = cellLines(ctx, p.cells, 'after');
      break;
    case 'records.created':
      key =
        p.source === 'duplicate'
          ? 'databases.history.events.recordsCreatedDuplicate'
          : 'databases.history.events.recordsCreatedImport';
      break;
    case 'format.applied': {
      const changes = list(p.changes).map(obj);
      const scope = t('databases.history.formatScope', {
        records: Number(p.record_count ?? 0),
        columns: Number(p.column_count ?? 0),
      });
      lines =
        changes.length > 0
          ? changes.map((change) => ({
              label: formatLabel(ctx, str(change.property)),
              before: show(ctx, change.before, undefined),
              after: show(ctx, change.after, undefined),
            }))
          : [{ text: scope }]; // a big one: every change is in the detail view
      break;
    }
    case 'column.created':
      lines = [
        { label: t('databases.history.fields.fieldType'), text: typeName(ctx, p.field_type) },
      ];
      break;
    case 'column.updated':
      lines = columnLines(ctx, obj(p.changes));
      break;
    case 'table.renamed':
      lines = [
        { label: t('databases.history.fields.name'), before: str(p.before), after: str(p.after) },
      ];
      break;
    case 'view.updated':
      lines = viewLines(ctx, obj(p.changes));
      break;
    case 'database.appearance_changed':
    case 'table.appearance_changed':
      lines = [
        {
          label: t('databases.history.fields.color'),
          before: paletteName(t, p.before),
          after: paletteName(t, p.after),
        },
      ];
      break;
    case 'database.renamed':
      lines = [
        { label: t('databases.history.fields.name'), before: str(p.before), after: str(p.after) },
      ];
      break;
    case 'database.updated': {
      const d = obj(obj(p.changes).description);
      lines = [
        {
          label: t('databases.history.fields.description'),
          before: show(ctx, d.before, undefined),
          after: show(ctx, d.after, undefined),
          long: true,
        },
      ];
      break;
    }
    case 'database.confidentiality_changed':
      lines = [
        {
          label: t('databases.history.fields.confidential'),
          before: show(ctx, p.before, undefined),
          after: show(ctx, p.after, undefined),
        },
      ];
      break;
    case 'database.export_finished':
    case 'table.export_finished': {
      const status = str(p.status) as keyof typeof EXPORT_STATUS;
      params.status = status in EXPORT_STATUS ? t(EXPORT_STATUS[status]) : status;
      break;
    }
    case 'database.copy_made':
    case 'table.copy_made':
      lines = [
        {
          text: p.include_records
            ? t('databases.history.modeData')
            : t('databases.history.modeStructure'),
        },
      ];
      break;
    case 'database.exported':
    case 'database.template_saved':
    case 'table.exported':
      lines = [
        {
          text: p.include_records
            ? t('databases.history.modeData')
            : t('databases.history.modeStructure'),
        },
      ];
      break;
    case 'access.member_role_set':
      lines = [
        {
          label: t('databases.history.fields.role'),
          before: role(ctx, p.before),
          after: role(ctx, p.after),
        },
      ];
      break;
    case 'access.member_removed':
    case 'access.member_left':
    case 'invitation.created':
    case 'invitation.accepted':
    case 'invitation.declined':
    case 'invitation.revoked':
    case 'invitation.resent':
      if (typeof p.role === 'string') {
        lines = [{ label: t('databases.history.fields.role'), text: role(ctx, p.role) }];
      }
      break;
    case 'access.rule_set':
      lines = [
        {
          label: t('databases.history.fields.rule'),
          before: role(ctx, p.before),
          after: role(ctx, p.after),
        },
      ];
      break;
    case 'access.role_permissions_changed':
      params.role = typeof p.role === 'string' ? role(ctx, p.role) : '';
      lines = [
        ...(list(p.added).length > 0
          ? [{ text: t('databases.history.granted', { list: list(p.added).join(', ') }) }]
          : []),
        ...(list(p.removed).length > 0
          ? [{ text: t('databases.history.withdrawn', { list: list(p.removed).join(', ') }) }]
          : []),
      ];
      break;
    case 'access.user_exceptions_set':
      lines = [
        ...(list(p.granted).length > 0
          ? [{ text: t('databases.history.granted', { list: list(p.granted).join(', ') }) }]
          : []),
        ...(list(p.revoked).length > 0
          ? [{ text: t('databases.history.withdrawn', { list: list(p.revoked).join(', ') }) }]
          : []),
      ];
      break;
    case 'access.workspace_changed': {
      const reason = str(p.reason) as keyof typeof REASONS;
      lines = [
        {
          text: reason in REASONS ? t(REASONS[reason]) : t('databases.history.reasons.other'),
        },
      ];
      break;
    }
    case 'database.duplicated':
    case 'database.imported':
      lines = [
        {
          text: t('databases.history.formatScope', {
            records: Number(p.records ?? 0),
            columns: Number(p.tables ?? 0),
          }),
        },
      ];
      break;
    default:
  }
  const sentence = t(key ?? 'databases.history.events.unknown', params);
  return { text: sentence, lines };
}
